import { useTranslation } from 'react-i18next';
import {
  BadgeTitle,
  BadgeValue,
  CardBody,
  InfoBadge,
  RightContent,
} from '../styles';
import Card from '@/components/Card';
import { DescriptionComponent, MainHeader } from '../Wireless/styles';
import { Skeleton, Typography } from '@mui/material';
import DescriptionPopper from '@/components/DescriptionPopper';
import { EditButton } from '../General/styles';
import { Pencil, WifiPen } from 'lucide-react';
import { useEquipmentContext } from '@/context/EquipmentContext';
import { voipFields } from '@/utils/constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ModalEditor from '@/components/ModalEditor';
import { getVoipSchema } from '@/validations/networkSchema';
import SelectAp from '../Wireless/SelectAp';
import api from '@/services/api';
import { enqueueSnackbar } from 'notistack';

function VoIP({ modeView }: { modeView: string }) {
  const { t } = useTranslation();

  const [voipData, setVoipData] = useState<VoipType[]>([]);
  const [currentVoip, setCurrentVoip] = useState(0);
  const [currentModal, setCurrentModal] = useState<
    'selectVoip' | 'editVoip' | null
  >(null);

  const { equipment, loading } = useEquipmentContext();

  const fetchVoipData = useCallback(async () => {
    try {
      const response = await api.get(`/devices/${equipment?.id}/voip`);
      setVoipData(response.data);
    } catch {
      console.error('Error fetching VoIP data');
    }
  }, [equipment]);

  useEffect(() => {
    fetchVoipData();
  }, [fetchVoipData]);

  /** Update on router and database VoIP changed fields */
  const handleEdit = async (
    interfaceType: 'voip',
    index: number,
    fieldsToCheck: { field: keyof NetworkInterfaceType; type: string }[],
    values: Record<string, string | number>
  ) => {
    if (!equipment || !equipment.model?.id) return;

    const interfaceData = voipData?.[index];

    const changedFields = fieldsToCheck
      .map((field) => {
        const current =
          interfaceData?.[field.field as keyof NetworkInterfaceType]?.value;
        const id =
          interfaceData?.[field.field as keyof NetworkInterfaceType]?.id;
        const newValue = values[field.field] ?? '';

        if (id && String(current ?? '') !== String(newValue ?? '')) {
          return { id, value: newValue };
        }
        return null;
      })
      .filter(Boolean) as { id: number; value: string | number }[];

    if (changedFields.length === 0) {
      setCurrentModal(null);
      return;
    }

    console.log(changedFields);
    try {
      const response = await api.patch(
        `/devices/${equipment.id}/set-parameters`,
        changedFields
      );

      if (response.status === 200) {
        await fetchVoipData();
        enqueueSnackbar(t('config.models.editSuccess'), {
          variant: 'success',
        });
        setCurrentModal(null);
      }
    } catch {
      enqueueSnackbar(t('config.models.editFailed'), { variant: 'error' });
    }
  };

  const handleEditVoip = (vals: Record<string, string | number>) =>
    handleEdit('voip', currentVoip, voipFields, vals);

  // Create externalOptions by extracting options from each VoIP field
  const voipExternalOptions = useMemo(() => {
    const voip = voipData[currentVoip];
    if (!voip) return {};

    const options: Record<string, { options: string[] }> = {};
    voipFields.forEach((field) => {
      const fieldData = voip[field.field as keyof NetworkInterfaceType];
      if (fieldData?.options && Array.isArray(fieldData.options)) {
        options[field.field] = { options: fieldData.options };
      }
    });

    return options;
  }, [voipData, currentVoip]);

  const voipSettingSchema = useMemo(() => getVoipSchema(t), [t]);

  return (
    <RightContent>
      <Card padding="24px">
        <MainHeader>
          <div className="divHeader">
            <Typography variant="h3">VoIP</Typography>
          </div>
          {/** Toggle buttons for switching between multiple VoIP interfaces */}
          <div className="divHeader">
            {voipData && voipData.length > 1 && (
              <DescriptionPopper
                description={
                  <DescriptionComponent>
                    {t('equipment.voip.selectVoip')}
                  </DescriptionComponent>
                }
                placement="top"
                children={
                  <EditButton onClick={() => setCurrentModal('selectVoip')}>
                    <WifiPen size={18} />
                  </EditButton>
                }
              />
            )}

            <DescriptionPopper
              description={
                <DescriptionComponent>
                  {t('equipment.voip.editVoip')}
                </DescriptionComponent>
              }
              placement="top"
              children={
                <EditButton onClick={() => setCurrentModal('editVoip')}>
                  <Pencil size={18} />
                </EditButton>
              }
            />
          </div>
        </MainHeader>

        {/** VoIP Information Body*/}
        <CardBody modeView={modeView === 'grid'}>
          {voipFields.map((field) => {
            const voip = voipData[currentVoip];
            const current =
              voip?.[field.field as keyof NetworkInterfaceType]?.value;

            // returns if voip doesn't contains previous configured fields
            if (voip?.[field.field as keyof NetworkInterfaceType] === null)
              return null;

            // Returns field's name and value in current selected VoIP interface
            return (
              <InfoBadge key={field.field} modeView={modeView}>
                <BadgeTitle>{t(`equipment.voip.${field.field}`)}</BadgeTitle>
                {!loading ? (
                  <BadgeValue empty={current === ''}>
                    {current === '' ? t('equipment.empty') : current}
                  </BadgeValue>
                ) : (
                  <Skeleton
                    variant="rounded"
                    width={modeView === 'grid' ? '225px' : '100%'}
                    height={14}
                  />
                )}
              </InfoBadge>
            );
          })}
        </CardBody>
      </Card>

      <ModalEditor
        open={currentModal === 'editVoip'}
        setOpen={setCurrentModal}
        title={t('equipment.voip.editVoip')}
        fieldList={voipFields}
        handleSubmit={handleEditVoip}
        fieldValues={voipData?.[currentVoip] ?? {}}
        translatePath="equipment.voip"
        externalOptions={voipExternalOptions}
        validationSchema={voipSettingSchema}
      />

      <SelectAp
        title={t('equipment.voip.changeVoip')}
        label={t('equipment.voip.selectVoip')}
        open={currentModal === 'selectVoip'}
        setOpen={setCurrentModal}
        currentValue={currentVoip}
        setCurrentValue={setCurrentVoip}
        currentOptions={voipData.map((_, index) => ({
          index,
          name: `VoIP ${index + 1}`,
        }))}
      />
    </RightContent>
  );
}

export default VoIP;
